/**
 * Loadout screen — pick a primary and a secondary from a 23-weapon roster, and read
 * any weapon's full stat sheet before you commit to it.
 *
 * ## Keyboard and gamepad are the primary input, not a fallback
 *
 * The game is playable with no pointing device at all (see `Input`), so this screen
 * is built as a focus list first and a clickable surface second. There is always a
 * visible cursor on exactly one row, arrows / D-pad / left stick move it, and one
 * confirm key equips. Every action reachable with the mouse — including opening,
 * scrolling and paging through the stat sheet — is reachable without it.
 *
 * ## Layout: a class rail and one long roster
 *
 * The six classes hold 6/6/2/3/3/3 weapons. A uniform grid of class cards was fine at
 * one gun per class and is not fine now: at six rows the AR card is three times the
 * height of the LMG card and the row of cards tears itself apart. So navigation stays
 * two-level but the *list* is one:
 *
 *   - the **rail** on the left names the six classes and how many of each you own;
 *   - the **roster** in the middle is every weapon, in class order, under sticky class
 *     headers, in one scroller — so what is coming is visible, not hidden behind a tab;
 *   - **Up/Down** walks the whole roster and crosses class boundaries;
 *   - **Left/Right** jumps a whole class at a time, keeping your position within the
 *     class where the class is deep enough and clamping where it is not.
 *
 * That last clause is where the uneven sizes actually bite. Jumping right from the
 * fifth SMG lands on the second and last LMG, not on nothing and not back at the top;
 * jumping back lands on the second SMG. Clamping rather than refusing means a player
 * holding Right walks the classes instead of getting stuck against the short one.
 *
 * ## Cost
 *
 * `update()` is a gamepad poll and nothing else, and returns on its first line when
 * the screen is closed: measured at 0.00001 ms/frame closed and 0.0003 ms/frame open
 * with no pad attached. No layout is read in the frame path — `scrollIntoView` runs
 * only on a cursor move, which is a keypress, never a frame, and the stat sheet's
 * markup including its SVG chart is built once on open (0.42 ms) and never again.
 *
 * Key events are taken on the **capture** phase with `stopImmediatePropagation`,
 * which is load-bearing: `Input` binds `keydown` on `window` and treats arrow keys as
 * look input. Ctrl/Cmd/Alt combinations are deliberately let through.
 *
 * Emits `loadout:set { primary, secondary }` via the model. Never reaches into
 * `WeaponSystem`; the model calls `ctx.weapons.setLoadout` only if it happens to
 * exist, and the event is the contract either way.
 */

import {
  COMBAT,
  KIND_GLYPH,
  KIND_LABEL,
  LEVEL_INITIAL,
  WEAPON_MAX_LEVEL,
  fieldOrigin,
  nextUnlockLevel,
  seconds,
  sourcePage,
  unlocksAtLevel,
  weaponFacts,
  weaponRow,
} from '../game/unlocks.data.js';
import { getLang, t } from './i18n.js';
import { EQUIPMENT } from '../game/equipment.data.js';
import { buildEquipmentModel } from '../weapons/models/equipment.js';
import { closeLabel, commandBar } from './CommandBar.js';

export const TUNING = {
  openKey: 'KeyB',
  sheetKey: 'KeyI', // 性能詳細
  gunsmithKey: 'KeyG', // fit parts to the weapon under the cursor
  showBeforeDeploy: true, // open once, automatically, the first time you deploy
  repeatDelay: 0.34, // s before a held direction starts repeating
  repeatRate: 0.11, // s between repeats
  padDeadzone: 0.5,
  sheetScrollStep: 96, // px per arrow press inside the stat sheet
};

/** Standard-mapping gamepad indices we read directly (Input does not expose these). */
const PAD = {
  A: 0,
  B: 1,
  X: 2,
  Y: 3,
  LB: 4,
  RB: 5,
  BACK: 8,
  START: 9,
  LS: 10, // left stick click — the equipment screen, beside its sibling on RS
  RS: 11, // right stick click — the Gunsmith, on a button nothing else on this screen wants
  UP: 12,
  DOWN: 13,
  LEFT: 14,
  RIGHT: 15,
};

export class LoadoutScreen {
  constructor(ctx, model) {
    this.ctx = ctx;
    this.model = model;

    this.isOpen = false;
    this.sheetOpen = false;
    /**
     * The Gunsmith, attached by `bootstrapProgression` after both exist.
     *
     * It is not constructed here and not imported: this screen is the *entry point* to
     * the Gunsmith, not its owner, and a null one has to be survivable because the two
     * are separate features that can be wired in either order. Every use is
     * optional-chained.
     */
    this.gunsmith = null;
    this.slot = 'primary'; // which slot the confirm key fills
    this.cursor = 0; // index into this.cells
    this.mode = 'menu'; // 'menu' | 'deploy' — only changes the footer wording

    const host = document.getElementById('ui-root') ?? document.body;
    const root = document.createElement('div');
    root.className = 'lo-root';
    host.appendChild(root);
    this.root = root;

    this._build();
    this._bindKeys();
    this._bindEvents();
    this._attachMenuEntry();

    this._repeat = { dir: 0, t: 0 };
    this._padPrev = [];
    this._unregister = ctx.engine?.registerUpdate?.((dt) => this.update(dt), 'ui');
  }

  /* -------------------------------- markup -------------------------------- */

  _build() {
    this.root.innerHTML = `
      <div class="lo-panel">
        <div class="lo-head">
          <div class="lo-title">LOADOUT</div>
          <div class="lo-rank">
            <span class="lo-rank-lv"><i>LV</i><b>1</b></span>
            <span class="lo-rank-track"><i></i></span>
            <span class="lo-rank-nums">0 / 0</span>
          </div>
        </div>
        <div class="lo-slots"></div>
        <div class="lo-body">
          <div class="lo-rail"></div>
          <div class="lo-list" tabindex="-1"></div>
          <div class="lo-side">
            <div class="lo-detail"></div>
            <div class="lo-next"></div>
          </div>
        </div>
        <div class="lo-foot"></div>
      </div>
      <div class="lo-sheet" aria-hidden="true">
        <div class="lo-sheet-panel">
          <div class="lo-sheet-head"></div>
          <div class="lo-sheet-body"></div>
          <div class="lo-sheet-foot"></div>
        </div>
      </div>`;

    this.panel = this.root.querySelector('.lo-panel');
    this.railEl = this.root.querySelector('.lo-rail');
    this.listEl = this.root.querySelector('.lo-list');
    this.slotsEl = this.root.querySelector('.lo-slots');
    this.detailEl = this.root.querySelector('.lo-detail');
    this.nextEl = this.root.querySelector('.lo-next');
    this.footEl = this.root.querySelector('.lo-foot');
    this.rankLv = this.root.querySelector('.lo-rank-lv b');
    this.rankFill = this.root.querySelector('.lo-rank-track i');
    this.rankNums = this.root.querySelector('.lo-rank-nums');
    this.sheetEl = this.root.querySelector('.lo-sheet');
    this.sheetHead = this.root.querySelector('.lo-sheet-head');
    this.sheetBody = this.root.querySelector('.lo-sheet-body');
    this.sheetFoot = this.root.querySelector('.lo-sheet-foot');

    this.rebuild();

    // Mouse is optional but supported. Hover moves the cursor so the two input
    // models never disagree about what is selected.
    this.listEl.addEventListener('mousemove', (e) => {
      const cell = e.target.closest('[data-cell]');
      if (!cell) return;
      const i = Number(cell.dataset.cell);
      if (i !== this.cursor) {
        this.cursor = i;
        this._render();
      }
    });
    this.listEl.addEventListener('click', (e) => {
      const cell = e.target.closest('[data-cell]');
      if (!cell) return;
      this.cursor = Number(cell.dataset.cell);
      this._confirm();
    });
    this.railEl.addEventListener('click', (e) => {
      const row = e.target.closest('[data-classindex]');
      if (!row) return;
      this._gotoClass(Number(row.dataset.classindex));
    });
    this.slotsEl.addEventListener('click', (e) => {
      if (e.target.closest('[data-act="equipment"]')) {
        this._openEquipment();
        return;
      }
      const chip = e.target.closest('[data-slot]');
      if (!chip) return;
      this.slot = chip.dataset.slot;
      this._render();
    });
    // The equipment screen publishes through the bus, so the chip stays true whether
    // the change came from that screen, from storage on boot, or from anywhere else.
    this.ctx.events.on('loadout:equipment', () => {
      if (this.isOpen) this._renderSlots();
    });
    this.detailEl.addEventListener('click', (e) => {
      if (e.target.closest('[data-act="sheet"]')) this._openSheet();
      if (e.target.closest('[data-act="gunsmith"]')) this._openGunsmith();
      if (e.target.closest('[data-act="equipment"]')) this._openEquipment();
    });
    this.sheetEl.addEventListener('click', (e) => {
      if (e.target === this.sheetEl || e.target.closest('[data-act="sheet-close"]')) {
        this._closeSheet();
      }
    });
  }

  /**
   * Build the rail and the roster from the model's current weapon list.
   *
   * Public because the roster is data that arrives from a file this feature does not
   * own: when the debug harness swaps the weapon source, this is how the screen picks
   * the new roster up without a reload. Built once per roster change — the frame path
   * only ever writes class names.
   */
  rebuild() {
    /** Flat cursor targets, in display order. */
    this.cells = [];
    /** `classGroups[ci]` = the cell indices belonging to class `ci`. */
    this.classGroups = [];
    /** `cellClass[i]` = which class group cell `i` is in. */
    this.cellClass = [];

    const classes = (this.model.classes ?? []).filter((c) => c);
    this.classes = classes;

    const list = [];
    const rail = [];
    let index = 0;

    for (let ci = 0; ci < classes.length; ci++) {
      const cls = classes[ci];
      const group = [];
      this.classGroups.push(group);
      const weapons = cls.weapons ?? [];

      list.push(`
        <div class="lo-cls" data-classhead="${ci}">
          <span class="lo-cls-tag">${esc(cls.label)}</span>
          <span class="lo-cls-name">${esc(cls.name)}</span>
          <span class="lo-cls-blurb">${esc(cls.blurb ?? '')}</span>
          <span class="lo-cls-count" data-count="${ci}"></span>
        </div>`);

      for (const w of weapons) {
        const i = index++;
        this.cells.push(w);
        this.cellClass.push(ci);
        group.push(i);
        list.push(`
          <div class="lo-wpn" data-cell="${i}">
            <span class="lo-wpn-rank">${esc(rankOf(w) ?? '—')}</span>
            <span class="lo-wpn-icon" data-icon="${esc(w.id)}"></span>
            <div class="lo-wpn-main">
              <div class="lo-wpn-name">${esc(w.name)}</div>
              <div class="lo-wpn-meta"></div>
            </div>
            <div class="lo-wpn-badge"></div>
          </div>`);
      }
      if (!weapons.length) {
        list.push('<div class="lo-wpn is-empty"><div class="lo-wpn-main"><div class="lo-wpn-name">—</div></div></div>');
      }

      rail.push(`
        <div class="lo-rail-item" data-classindex="${ci}">
          <span class="lo-rail-tag">${esc(cls.label)}</span>
          <span class="lo-rail-name">${esc(cls.name)}</span>
          <span class="lo-rail-count"></span>
        </div>`);
    }

    this.listEl.innerHTML = list.join('');
    this.railEl.innerHTML = rail.join('');
    this.cellEls = Array.from(this.listEl.querySelectorAll('[data-cell]'));
    this.headEls = Array.from(this.listEl.querySelectorAll('[data-classhead]'));
    this.railEls = Array.from(this.railEl.querySelectorAll('[data-classindex]'));
    this.countEls = Array.from(this.railEl.querySelectorAll('.lo-rail-count'));
    this.headCountEls = Array.from(this.listEl.querySelectorAll('[data-count]'));

    if (this.cursor >= this.cells.length) this.cursor = 0;
    // After the rows exist, and before the first render, so the observer is watching
    // elements that are actually in the document.
    this._fillIcons();
    if (this.isOpen) this._render();
  }

  /* ------------------------------ open / close ----------------------------- */

  open(mode = 'menu') {
    if (this.isOpen) return;
    // The deploy auto-open is on a timer. A player who reached the Gunsmith first
    // must not have the roster shoved under it a moment later.
    if (this.gunsmith?.isOpen) return;
    this.isOpen = true;
    this.mode = mode;

    // Put the cursor on the equipped primary rather than at 0 — the player's own
    // choice is the sensible starting point every time.
    const i = this.cells.findIndex((w) => w.id === this.model.primary);
    if (i >= 0) this.cursor = i;
    this.slot = 'primary';

    this.root.classList.add('is-open');
    this._render();
    // The cursor may be deep in the roster; put it on screen before the first paint.
    requestAnimationFrame(() => this._scrollToCursor());

    // Stop the world. The engine pauses on `game:state menu`, which is the same
    // mechanism the pause menu uses — a loadout screen over a live firefight is a
    // bug, not a feature.
    if (this.ctx.engine && !this.ctx.engine.paused) {
      this.ctx.events.emit('game:state', { state: 'menu' });
    }
    // Release the mouse so the pointer is usable; keyboard players are unaffected.
    if (document.pointerLockElement) document.exitPointerLock?.();
    this.ctx.events.emit('audio:play', { id: 'ui_open', volume: 0.4 });
  }

  close() {
    if (!this.isOpen) return;
    if (this.sheetOpen) this._closeSheet();
    this.isOpen = false;
    this.root.classList.remove('is-open');

    // Hand control back the same way the pause menu does. `Menu` is another agent's
    // file, so everything here is optional-chained and there is an event fallback.
    const menu = this.ctx.hud?.menu;
    if (menu && menu.screen === 'pause') menu.hide();
    else this.ctx.events.emit('game:state', { state: 'playing' });
    // Unconditionally, exactly as the pause menu's Resume does: this screen can be
    // reached from the pause menu, where the lock is already gone, so "only re-grab
    // it if we took it" would drop a mouse player back into the game with a loose
    // cursor. `requestLock` is a no-op when the lock is already held.
    //
    // Swallowed rather than awaited. `requestPointerLock` rejects whenever the browser
    // will not grant it — the document is not the active one, Chrome's post-Escape
    // cooldown is still running, an automation harness is driving — and a menu closing
    // is not a failure just because the mouse stayed free. Today's `Input.requestLock`
    // drops the promise internally so the rejection surfaces from there and this
    // cannot catch it; the guard is here so that an `Input` which does return it
    // never turns a routine close into an uncaught error.
    try {
      this.ctx.input?.requestLock?.()?.catch?.(() => {});
    } catch {
      /* an Input that throws synchronously must not strand the player on this screen */
    }
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  /* -------------------------------- input ---------------------------------- */

  _bindKeys() {
    // Capture phase: see the class comment. Without this the arrow keys reach
    // `Input` and turn the camera behind the screen.
    // `stopImmediatePropagation` as well as `stopPropagation`: capture on `window`
    // beats a bubble listener on `window` only when the event has a deeper target.
    // A key event dispatched *at* `window` — which is what a synthetic test does,
    // and what some assistive tech does — arrives AT_TARGET, where both phases run
    // in registration order and `Input` registered first. Immediate is the only
    // form that holds in both cases.
    const swallow = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };

    this._onKey = (e) => {
      // Ctrl / Cmd / Alt combinations are the browser's, not ours. The open screen
      // swallows every bare key so nothing leaks to `Input`, and without this guard
      // that would include Cmd-R, Cmd-W and the devtools shortcuts.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // The Gunsmith opens *over* this screen and deliberately leaves it up, so that
      // closing it puts the player back on the roster row they came from. This
      // listener is on `window` in the capture phase and was registered first, so
      // without this line it would swallow every key — including the arrows — before
      // the screen actually on top ever saw them, and the Gunsmith would be dead to
      // the keyboard. Whichever screen is on top handles the key; this one steps back.
      if (this.gunsmith?.isOpen) return;
      // Same for the equipment screen, and for exactly the same reason: it registers
      // its capture listener after this one, so without stepping back here it would
      // never receive a key.
      if (this.ctx.equipmentScreen?.isOpen) return;
      // ...and for match setup and the friend-match screen, which are opened from
      // this screen's own footer. Leaving them off this list is why their footer
      // buttons did nothing at all: the bar synthesises the key it advertises, and
      // this listener ate it before the screen on top could act on it.
      if (this.ctx.modeSelect?.open) return;
      if (this.ctx.multiplayer?.open) return;

      if (!this.isOpen) {
        if (e.code === TUNING.openKey && !e.repeat) {
          swallow(e);
          this.open();
        }
        return;
      }
      const handled = this.sheetOpen
        ? this._handleSheetKey(e.code, e.shiftKey, e.repeat)
        : this._handleKey(e.code, e.shiftKey, e.repeat);
      if (handled) swallow(e);
    };
    window.addEventListener('keydown', this._onKey, true);
  }

  _handleKey(code, shift, repeat) {
    switch (code) {
      case 'ArrowLeft':
      case 'KeyA':
        this._move(-1, 0);
        return true;
      case 'ArrowRight':
      case 'KeyD':
        this._move(1, 0);
        return true;
      case 'ArrowUp':
      case 'KeyW':
        this._move(0, -1);
        return true;
      case 'ArrowDown':
      case 'KeyS':
        this._move(0, 1);
        return true;
      // Class-at-a-time paging for a long roster, on the keys a player already
      // associates with it. Home / End go to the ends of the whole list.
      case 'PageUp':
        this._move(-1, 0);
        return true;
      case 'PageDown':
        this._move(1, 0);
        return true;
      case 'Home':
        this._jumpTo(0);
        return true;
      case 'End':
        this._jumpTo(this.cells.length - 1);
        return true;

      case 'Enter':
      case 'NumpadEnter':
      case 'Space':
      case 'KeyE':
      case 'KeyF':
        if (repeat) return true;
        // Shift+confirm fills the other slot without having to switch slots first.
        this._confirm(shift ? other(this.slot) : this.slot);
        return true;

      case TUNING.sheetKey:
      case 'Slash': // '?' — the other key a player tries when they want to know more
        if (repeat) return true;
        this._openSheet();
        return true;

      // The Gunsmith is dispatched from here rather than from its own window listener
      // because this screen swallows every key while it is open — deliberately, so
      // nothing leaks to `Input` — and a second listener would therefore never see it.
      case TUNING.gunsmithKey:
        if (repeat) return true;
        this._openGunsmith();
        return true;

      // Same reasoning as the Gunsmith above: this screen swallows every key, so the
      // equipment screen cannot listen for its own opener while this one is up.
      //
      // `T` for throwables, and emphatically not `E`, which this switch has aliased
      // to "equip the weapon under the cursor" since long before there was any
      // equipment screen. A `switch` takes the first matching `case`, so a second
      // `case 'KeyE'` further down was unreachable — silently, with no warning from
      // the parser or the linter. Tapping the chip therefore re-equipped the weapon
      // that was already equipped, which looks exactly like a dead button.
      case 'KeyT':
        if (repeat) return true;
        this._openEquipment();
        return true;

      /**
       * Match setup, routed from here rather than owned by that screen.
       *
       * This screen claims keys on `window` in the capture phase and stops them
       * there, so a listener anywhere further down never sees them — measured: a
       * probe on `document` capture recorded nothing at all while the loadout was
       * open, and the footer button appeared to do nothing. The screen that owns
       * the keyboard is the screen that has to hand the key on, exactly as it
       * already does for the gunsmith and the equipment screens.
       */
      case 'KeyM':
        if (repeat) return true;
        this.ctx.modeSelect?.show();
        return true;

      // Same reason as KeyM: this screen swallows keys on `window` capture, so
      // anything reachable by a key has to be handed on from here.
      case 'KeyP':
        if (repeat) return true;
        this.ctx.multiplayer?.show();
        return true;

      case 'Tab':
      case 'KeyQ':
        if (repeat) return true;
        this.slot = other(this.slot);
        this._render();
        return true;

      case 'Escape':
      case 'Backspace':
      case TUNING.openKey:
        if (repeat) return true;
        this.close();
        return true;

      default:
        // Swallow everything else too: a keystroke that leaks to `Input` while this
        // screen is up is a keystroke that moves the player.
        return true;
    }
  }

  /**
   * Movement over the roster.
   *
   * Vertical walks the flat list and wraps at both ends, crossing class boundaries —
   * one list, one cursor, no dead ends. Horizontal jumps a whole class and keeps the
   * player's position *within* the class, clamped to the shorter of the two: from the
   * fifth AR you land on the second and last LMG, not on nothing and not back at the
   * top. Both are the behaviours a player can predict from one press.
   */
  _move(dx, dy) {
    const n = this.cells.length;
    if (!n) return;

    let next = this.cursor;
    if (dy) next = (this.cursor + dy + n) % n;

    if (dx) {
      const ci = this.cellClass[this.cursor] ?? 0;
      const groups = this.classGroups;
      const count = groups.length;
      if (!count) return;
      const slot = groups[ci]?.indexOf(this.cursor) ?? 0;
      // Skip empty classes rather than landing on nothing.
      let target = ci;
      for (let step = 0; step < count; step++) {
        target = (target + dx + count) % count;
        if (groups[target]?.length) break;
      }
      const g = groups[target];
      if (!g?.length) return;
      next = g[Math.min(Math.max(0, slot), g.length - 1)];
    }

    if (next === this.cursor) return;
    this.cursor = next;
    this._render();
    this._scrollToCursor();
    this.ctx.events.emit('audio:play', { id: 'ui_move', volume: 0.25 });
  }

  /** Absolute jump, used by Home / End and by the rail. */
  _jumpTo(i) {
    const n = this.cells.length;
    if (!n) return;
    const next = Math.max(0, Math.min(n - 1, i));
    if (next === this.cursor) return;
    this.cursor = next;
    this._render();
    this._scrollToCursor();
    this.ctx.events.emit('audio:play', { id: 'ui_move', volume: 0.25 });
  }

  _gotoClass(ci) {
    const g = this.classGroups[ci];
    if (!g?.length || g[0] === this.cursor) return;
    this.cursor = g[0];
    this._render();
    this._scrollToCursor();
  }

  /**
   * Keep the cursor on screen. A layout operation, deliberately confined to the
   * keypress path — the frame loop never touches it.
   */
  _scrollToCursor() {
    const el = this.cellEls[this.cursor];
    if (!el) return;
    // Landing on the first weapon of a class should show the class header too,
    // otherwise a jump reads as "the list moved" rather than "I changed class".
    const ci = this.cellClass[this.cursor];
    const g = this.classGroups[ci];
    if (g && g[0] === this.cursor && this.headEls[ci]) {
      this.headEls[ci].scrollIntoView({ block: 'nearest' });
    }
    el.scrollIntoView({ block: 'nearest' });
  }

  _confirm(slot = this.slot) {
    const w = this.cells[this.cursor];
    if (!w) return;
    if (w.pending || !this.model.isUnlocked(w.id)) {
      this.panel.classList.remove('is-deny');
      // Reflow-free restart of the shake: toggling the class in a rAF is enough,
      // and we are not in the frame loop here.
      requestAnimationFrame(() => this.panel.classList.add('is-deny'));
      this.ctx.events.emit('audio:play', { id: 'ui_deny', volume: 0.4 });
      return;
    }

    if (slot === 'secondary') {
      this.model.setLoadout(this.model.primary, w.id);
    } else {
      // Equipping the current secondary as primary swaps them rather than blanking
      // the second slot, which is what a player expects from a two-gun loadout.
      const sec = this.model.secondary === w.id ? this.model.primary : this.model.secondary;
      this.model.setLoadout(w.id, sec);
    }
    this._render();
    if (this.sheetOpen) this._renderSheetFoot();
    this.ctx.events.emit('audio:play', { id: 'ui_confirm', volume: 0.5 });
  }

  /**
   * Hand the weapon under the cursor to the Gunsmith.
   *
   * Refused for a locked or unbuilt weapon with the same shake the equip path uses:
   * a screen that opens an empty parts list for a gun you do not own has told you
   * nothing and cost you two keypresses.
   */
  /**
   * Hand over to the equipment screen and take the screen back when it closes.
   *
   * The loadout hides rather than closes: closing would deploy the player, and
   * "I went to look at my grenades" is not "I am ready".
   */
  _openEquipment() {
    const screen = this.ctx.equipmentScreen;
    if (!screen) return;
    this.root.classList.remove('is-open');
    screen.onClose = () => {
      screen.onClose = null;
      this.root.classList.add('is-open');
      this._render();
    };
    screen.open();
  }

  _openGunsmith() {
    const w = this.cells[this.cursor];
    if (!this.gunsmith || !w || w.pending || !this.model.isUnlocked(w.id)) {
      this.panel.classList.remove('is-deny');
      requestAnimationFrame(() => this.panel.classList.add('is-deny'));
      this.ctx.events.emit('audio:play', { id: 'ui_deny', volume: 0.4 });
      return;
    }
    this.gunsmith.open(w.id, { fromLoadout: true });
  }

  /* ------------------------------- stat sheet ------------------------------ */

  _openSheet() {
    const w = this.cells[this.cursor];
    if (!w || this.sheetOpen) return;
    this.sheetOpen = true;
    this.sheetEl.classList.add('is-open');
    this.sheetEl.setAttribute('aria-hidden', 'false');
    this._renderSheet();
    this.sheetBody.scrollTop = 0;
    this.ctx.events.emit('audio:play', { id: 'ui_open', volume: 0.3 });
  }

  _closeSheet() {
    if (!this.sheetOpen) return;
    this.sheetOpen = false;
    this.sheetEl.classList.remove('is-open');
    this.sheetEl.setAttribute('aria-hidden', 'true');
    this.ctx.events.emit('audio:play', { id: 'ui_move', volume: 0.25 });
  }

  /**
   * The sheet is a second focus context, not a dialog the player has to mouse out of.
   * Up/Down scroll it, Left/Right page to the neighbouring weapon so a player can
   * compare two guns without closing anything, and confirm still equips.
   */
  _handleSheetKey(code, shift, repeat) {
    const body = this.sheetBody;
    switch (code) {
      case 'ArrowUp':
      case 'KeyW':
        body.scrollTop -= TUNING.sheetScrollStep;
        return true;
      case 'ArrowDown':
      case 'KeyS':
        body.scrollTop += TUNING.sheetScrollStep;
        return true;
      case 'PageUp':
        body.scrollTop -= body.clientHeight * 0.9;
        return true;
      case 'PageDown':
        body.scrollTop += body.clientHeight * 0.9;
        return true;
      case 'Home':
        body.scrollTop = 0;
        return true;
      case 'End':
        body.scrollTop = body.scrollHeight;
        return true;

      case 'ArrowLeft':
      case 'KeyA':
        this._sheetStep(-1);
        return true;
      case 'ArrowRight':
      case 'KeyD':
        this._sheetStep(1);
        return true;

      case 'Tab':
      case 'KeyQ':
        if (repeat) return true;
        this.slot = other(this.slot);
        this._renderSlots();
        this._renderSheetFoot();
        return true;

      // The stat sheet is where a player decides they want this gun; letting them go
      // straight from there to fitting it saves a close they would only undo.
      case TUNING.gunsmithKey:
        if (repeat) return true;
        this._closeSheet();
        this._openGunsmith();
        return true;

      case 'Enter':
      case 'NumpadEnter':
      case 'Space':
      case 'KeyE':
      case 'KeyF':
        if (repeat) return true;
        this._confirm(shift ? other(this.slot) : this.slot);
        return true;

      case 'Escape':
      case 'Backspace':
      case TUNING.sheetKey:
      case TUNING.openKey:
        if (repeat) return true;
        this._closeSheet();
        return true;

      default:
        return true;
    }
  }

  /** Move to the neighbouring weapon with the sheet still up. */
  _sheetStep(d) {
    const n = this.cells.length;
    if (!n) return;
    this.cursor = (this.cursor + d + n) % n;
    this._render();
    this._scrollToCursor();
    this._renderSheet();
    this.sheetBody.scrollTop = 0;
    this.ctx.events.emit('audio:play', { id: 'ui_move', volume: 0.25 });
  }

  /* -------------------------------- gamepad -------------------------------- */

  update(dt) {
    // Same rule as the key handler: the screen on top drives the pad. Polling here
    // while the Gunsmith is up would move both cursors on one stick push.
    if (!this.isOpen || this.gunsmith?.isOpen) return;
    this._pollPad(dt);
  }

  /**
   * `Input.pollGamepad` runs on the 'input' phase and only maps buttons onto
   * gameplay actions, so the menu reads the pad directly. Same edge-detection idea,
   * plus a repeat timer so a held stick scrolls at a usable rate.
   */
  _pollPad(dt) {
    const pads = navigator.getGamepads?.() ?? [];
    let gp = null;
    for (const p of pads) if (p && p.connected) { gp = p; break; }
    if (!gp) return;

    const btn = (i) => !!(gp.buttons[i]?.pressed || gp.buttons[i]?.value > 0.5);
    const pressed = (i) => {
      const now = btn(i);
      const was = !!this._padPrev[i];
      this._padPrev[i] = now;
      return now && !was;
    };

    // Every edge is sampled before any of them is acted on. Short-circuiting an
    // `||` here would skip the `_padPrev` update for the buttons after it, and a
    // button whose previous state is never refreshed fires again on the next frame.
    const hitA = pressed(PAD.A);
    const hitX = pressed(PAD.X);
    const hitY = pressed(PAD.Y);
    const hitB = pressed(PAD.B);
    const hitLB = pressed(PAD.LB);
    const hitRB = pressed(PAD.RB);
    const hitSelect = pressed(PAD.BACK);
    const hitStart = pressed(PAD.START);
    const hitRS = pressed(PAD.RS);
    const hitLS = pressed(PAD.LS);

    if (hitRS) {
      this._closeSheet();
      this._openGunsmith();
      return;
    }
    // The pad needs its own route too, or the footer promises a button that does
    // nothing — which is how the "ESC" close buttons went wrong.
    if (hitLS) {
      this._closeSheet();
      this._openEquipment();
      return;
    }
    if (hitY) {
      this.sheetOpen ? this._closeSheet() : this._openSheet();
    }
    if (hitA) this._confirm(this.slot);
    if (hitX) this._confirm(other(this.slot));
    if (hitSelect) {
      this.slot = other(this.slot);
      this._renderSlots();
      if (this.sheetOpen) this._renderSheetFoot();
      else this._renderFoot();
    }
    if (hitLB) this.sheetOpen ? this._sheetStep(-1) : this._move(-1, 0);
    if (hitRB) this.sheetOpen ? this._sheetStep(1) : this._move(1, 0);
    if (hitB || hitStart) {
      if (this.sheetOpen) this._closeSheet();
      else {
        this.close();
        return;
      }
    }

    // D-pad and left stick share one repeat timer, so holding either scrolls.
    const lx = gp.axes[0] ?? 0;
    const ly = gp.axes[1] ?? 0;
    const dz = TUNING.padDeadzone;
    let dx = (btn(PAD.RIGHT) ? 1 : 0) - (btn(PAD.LEFT) ? 1 : 0);
    let dy = (btn(PAD.DOWN) ? 1 : 0) - (btn(PAD.UP) ? 1 : 0);
    if (!dx && Math.abs(lx) > dz) dx = Math.sign(lx);
    if (!dy && Math.abs(ly) > dz) dy = Math.sign(ly);

    const dir = dx * 3 + dy;
    if (!dx && !dy) {
      this._repeat.dir = 0;
      this._repeat.t = 0;
      return;
    }
    const step = () => {
      if (this.sheetOpen) {
        if (dy) this.sheetBody.scrollTop += dy * TUNING.sheetScrollStep;
        else if (dx) this._sheetStep(dx);
      } else {
        this._move(dx, dy);
      }
    };
    if (dir !== this._repeat.dir) {
      this._repeat.dir = dir;
      this._repeat.t = TUNING.repeatDelay;
      step();
      return;
    }
    this._repeat.t -= dt;
    if (this._repeat.t <= 0) {
      this._repeat.t = TUNING.repeatRate;
      step();
    }
  }

  /* -------------------------------- events --------------------------------- */

  _bindEvents() {
    const ev = this.ctx.events;
    ev.on('xp:gain', () => this.isOpen && this._renderRank());
    ev.on('level:up', () => this.isOpen && this._render());

    if (TUNING.showBeforeDeploy) {
      // Class select before the first spawn, the way a lobby does it. Once per page
      // load only — nobody wants this screen every time they respawn.
      const off = ev.on('input:lock', ({ locked } = {}) => {
        if (!locked) return;
        off();
        setTimeout(() => this.open('deploy'), 60);
      });
    }
  }

  /**
   * Add a LOADOUT entry to the pause menu.
   *
   * `Menu` replaces the contents of `.menu-panel` when it switches screens, so a
   * button appended once would vanish. The observer is idle except on those swaps.
   */
  _attachMenuEntry() {
    const tryAttach = () => {
      const panel = document.querySelector('.hud-menu .menu-panel');
      if (!panel) return false;
      if (!this._observer) {
        this._observer = new MutationObserver(() => this._injectButton(panel));
        this._observer.observe(panel, { childList: true, subtree: true });
      }
      this._injectButton(panel);
      return true;
    };
    // The HUD may not have built its menu yet depending on wiring order.
    if (!tryAttach()) setTimeout(tryAttach, 400);
  }

  _injectButton(panel) {
    // Only the pause screen: the start screen is deliberately click-through so its
    // click can reach the canvas, and the death screen should not detour here.
    const resume = panel.querySelector('[data-act="resume"]');
    if (!resume) return;
    const host = resume.parentElement;
    if (!host || host.querySelector('[data-act="loadout"]')) return;

    const btn = document.createElement('button');
    btn.className = 'menu-btn';
    btn.dataset.act = 'loadout';
    btn.textContent = 'Loadout';
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.open();
    });
    resume.after(btn);
  }

  /* -------------------------------- render --------------------------------- */

  _render() {
    this._renderRank();
    this._renderSlots();
    this._renderCells();
    this._renderDetail();
    this._renderNext();
    this._renderFoot();
  }

  _renderRank() {
    const m = this.model;
    this.rankLv.textContent = String(m.level);
    this.rankFill.style.transform = `scaleX(${m.maxed ? 1 : m.levelFraction.toFixed(3)})`;
    this.rankNums.textContent = m.maxed
      ? `MAX RANK`
      : `${fmt(m.xpIntoLevel)} / ${fmt(m.xpForNext)} XP`;
    this.root.classList.toggle('is-max', m.maxed);
  }

  _renderSlots() {
    const m = this.model;
    const name = (id) => this.cells.find((w) => w.id === id)?.name ?? 'NONE';
    const guns = ['primary', 'secondary']
      .map(
        (s) =>
          `<div class="lo-slot${this.slot === s ? ' is-active' : ''}" data-slot="${s}">
             <span>${s.toUpperCase()}</span>
             <b>${esc(name(s === 'primary' ? m.primary : m.secondary))}</b>
           </div>`
      )
      .join('');
    this.slotsEl.innerHTML = guns + this._equipChip();
  }

  /**
   * The third chip: what is in the two equipment slots, with pictures.
   *
   * It sits beside the guns because that is where a player looks to answer "what am
   * I taking in", and equipment is half of that answer. Before this the only way to
   * find out was to open a separate screen, so the loadout screen was quietly
   * showing two thirds of the loadout.
   *
   * Icons rather than names alone, because the point is recognition at a glance —
   * the twelve pieces were given distinct silhouettes for exactly this, and a
   * player who has thrown a mine once knows the disc without reading.
   */
  _equipChip() {
    // The *simulation*, not the equipment screen. Both hold the same choice, but
    // they update in listener-registration order on `loadout:equipment` — Equipment
    // is constructed first and this screen second, with the equipment screen third —
    // so reading the screen here repainted the chip with the value it was about to
    // stop having. Reading what the player will actually throw is both correct and
    // free of the ordering question.
    const eq = this.ctx.equipment?.loadout ?? this.ctx.equipmentScreen?.selection;
    if (!eq) return '';
    const icons = this.ctx.icons;

    const one = (slot) => {
      const def = EQUIPMENT[eq[slot]];
      if (!def) return '';
      const url = icons?.object?.(`eq:${def.id}`, () => buildEquipmentModel(def.id));
      const label = (getLang() === 'en' ? def.nameEn || def.name : def.name) ?? def.id;
      return `<div class="lo-eq-one">
          <i class="lo-eq-icon"${url ? ` style="background-image:url(${url})"` : ''}></i>
          <u>${esc(label)}</u>
        </div>`;
    };

    // `data-act` rather than `data-slot`: this chip opens a screen, it does not
    // change which gun the confirm key fills, and sharing the attribute would put it
    // into the slot cycle where it does not belong.
    return `<div class="lo-slot lo-slot-eq" data-act="equipment" role="button" tabindex="0">
        <span>${esc(t('cmd.equipment'))}<em>T</em></span>
        <div class="lo-eq-pair">${one('lethal')}${one('tactical')}</div>
      </div>`;
  }

  _renderCells() {
    const m = this.model;
    // One pass over the roster rather than a `find` per cell: `isUnlocked` is a
    // linear scan, and calling it 23 times inside a 23-iteration loop is the kind of
    // quadratic that only shows up once the roster grows.
    const owned = new Set();
    for (const w of m.weapons ?? []) if (m.isUnlocked(w.id)) owned.add(w.id);

    const perClass = new Array(this.classGroups.length).fill(0);

    for (let i = 0; i < this.cellEls.length; i++) {
      const el = this.cellEls[i];
      const w = this.cells[i];
      if (!el || !w) continue;
      const unlocked = owned.has(w.id);
      if (unlocked) perClass[this.cellClass[i]]++;

      el.classList.toggle('is-cursor', i === this.cursor);
      el.classList.toggle('is-locked', !unlocked && !w.pending);
      el.classList.toggle('is-pending', !!w.pending);
      el.classList.toggle('is-primary', m.primary === w.id);
      el.classList.toggle('is-secondary', m.secondary === w.id);

      const meta = el.querySelector('.lo-wpn-meta');
      const badge = el.querySelector('.lo-wpn-badge');
      if (meta) meta.textContent = metaLine(w, unlocked);
      if (badge) {
        badge.textContent =
          m.primary === w.id
            ? 'PRIMARY'
            : m.secondary === w.id
              ? 'SECONDARY'
              : w.pending
                ? 'SOON'
                : unlocked
                  ? ''
                  : `LV ${w.level}`;
      }
    }

    for (let ci = 0; ci < this.classGroups.length; ci++) {
      const total = this.classGroups[ci].length;
      const text = `${perClass[ci]} / ${total}`;
      if (this.countEls[ci]) this.countEls[ci].textContent = text;
      if (this.headCountEls[ci]) this.headCountEls[ci].textContent = text;
      this.railEls[ci]?.classList.toggle('is-active', ci === this.cellClass[this.cursor]);
      this.railEls[ci]?.classList.toggle('is-complete', total > 0 && perClass[ci] === total);
      this.headEls[ci]?.classList.toggle('is-active', ci === this.cellClass[this.cursor]);
    }
  }

  /**
   * Fill weapon portraits as rows scroll into view.
   *
   * Not all at once: each icon is a full weapon model built, rendered and thrown
   * away, and doing twenty-three of those in the frame the screen opens would be a
   * visible stall on a phone. An observer means a player who never scrolls past the
   * assault rifles never pays for the sniper portraits.
   */
  _fillIcons() {
    const forge = this.ctx.icons;
    if (!forge) return;
    this._iconObs?.disconnect();

    const paint = (el) => {
      if (el.dataset.painted) return;
      const url = forge.weapon(el.dataset.icon);
      el.dataset.painted = '1';
      if (url) el.style.backgroundImage = `url(${url})`;
      else el.classList.add('is-blank'); // a model that would not build; no icon, no fuss
    };

    if (typeof IntersectionObserver !== 'function') {
      for (const el of this.listEl.querySelectorAll('[data-icon]')) paint(el);
      return;
    }
    this._iconObs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          paint(e.target);
          this._iconObs.unobserve(e.target);
        }
      },
      { root: this.listEl, rootMargin: '200px' }
    );
    for (const el of this.listEl.querySelectorAll('[data-icon]')) this._iconObs.observe(el);
  }

  _renderDetail() {
    const w = this.cells[this.cursor];
    const m = this.model;
    if (!w) {
      this.detailEl.innerHTML = '';
      return;
    }
    const unlocked = m.isUnlocked(w.id);
    const bars = w.stats
      ? [
          ['DAMAGE', w.stats.damage],
          ['FIRE RATE', w.stats.rate],
          ['RANGE', w.stats.range],
          ['HANDLING', w.stats.handling],
        ]
          .map(
            ([label, v]) =>
              `<div class="lo-stat"><span>${label}</span><i><b style="transform:scaleX(${num(v).toFixed(3)})"></b></i></div>`
          )
          .join('')
      : '<div class="lo-stat-none">NO DATA — WEAPON NOT YET BUILT</div>';

    const status = w.pending
      ? `<div class="lo-detail-status is-pending">NOT YET AVAILABLE${
          w.level === LEVEL_INITIAL ? '' : ` · UNLOCKS AT LEVEL ${w.level}`
        }</div>`
      : unlocked
        ? `<div class="lo-detail-status is-ok">${
            w.level === LEVEL_INITIAL ? 'STANDARD ISSUE' : 'AVAILABLE'
          }</div>`
        : `<div class="lo-detail-status is-locked">UNLOCKS AT LEVEL ${w.level}</div>`;

    const rank = rankOf(w);
    this.detailEl.innerHTML = `
      <div class="lo-detail-top">
        <div class="lo-detail-class">${esc(w.className ?? '')}</div>
        ${rank ? `<div class="lo-detail-rank" title="PUBLISHED RANK">${esc(rank)}</div>` : ''}
      </div>
      <div class="lo-detail-name">${esc(w.name)}</div>
      ${status}
      <div class="lo-stats">${bars}</div>
      ${gunsmithLine(this.gunsmith, w, unlocked)}
      <button class="lo-detail-btn" type="button" data-act="sheet">
        <span>性能詳細</span><i>I</i>
      </button>
      <button class="lo-detail-btn${unlocked && !w.pending ? '' : ' is-off'}" type="button" data-act="gunsmith">
        <span>GUNSMITH</span><i>G</i>
      </button>
      <!-- Desktop's route to the equipment screen. It had one on touch and none
           here, because the touch chip bar and the keyboard hint line are two
           separate code paths and only the first was updated — so on a desktop the
           screen existed and was unreachable without knowing the key. -->
      <button class="lo-detail-btn is-equip" type="button" data-act="equipment">
        <span>${t('cmd.equipment')}</span><i>T</i>
      </button>`;
  }

  _renderNext() {
    const m = this.model;
    const at = nextUnlockLevel(m.level);
    if (!at) {
      this.nextEl.innerHTML = `<div class="lo-next-head">${t('lo.maxRank')}</div>
        <div class="lo-next-body">${t('lo.maxRankBody')}</div>`;
      return;
    }
    // Names on these entries are already resolved against the weapon data by
    // `unlocks.data.js`; nothing here re-derives them from the class.
    const items = unlocksAtLevel(at)
      .map(
        (u) =>
          `<div class="lo-next-item"><i>${KIND_GLYPH[u.kind] ?? '·'}</i><span>${
            KIND_LABEL[u.kind] ?? ''
          }</span><b>${esc(u.name ?? '')}</b>${
            u.rank ? `<u>${esc(u.rank)}</u>` : ''
          }</div>`
      )
      .join('');
    const away = at - m.level;
    const xpAway = away === 1 ? m.xpForNext - m.xpIntoLevel : null;
    this.nextEl.innerHTML = `
      <div class="lo-next-head">NEXT UNLOCK · LEVEL ${at}</div>
      ${items}
      <div class="lo-next-foot">${
        xpAway != null ? `${fmt(xpAway)} XP AWAY` : `${away} LEVELS AWAY`
      }</div>`;
  }

  _renderFoot() {
    // One bar, every device. The hint line this replaced looked like a control and
    // refused to act like one on the only platform that has a cursor — and being two
    // code paths, it was also where the equipment route went missing on desktop.
    this.footEl.replaceChildren(
      commandBar(
        [
          { key: this.mode === 'deploy' ? 'cmd.deploy' : 'cmd.close', code: 'Escape', pad: 'B', primary: true },
          { key: 'cmd.equip', code: 'Enter', pad: 'A' },
          { key: 'cmd.gunsmith', code: 'KeyG', pad: 'RS' },
          { key: 'cmd.equipment', code: 'KeyT', pad: 'LS' },
          // The only route to match setup that a player can find without being
          // told a key. A screen reachable solely by pressing M is a screen that
          // does not exist on a phone, which is the same defect the equipment
          // screen shipped with.
          { key: 'cmd.modes', code: 'KeyM', pad: 'START' },
          { key: 'cmd.multiplayer', code: 'KeyP', pad: 'X' },
          { key: 'cmd.stats', code: 'KeyI', pad: 'Y' },
          { key: 'cmd.otherSlot', code: 'Tab', pad: 'SEL' },
        ],
        { padConnected: !!this.ctx.input?.pad?.connected }
      )
    );
  }


  /* ----------------------------- the stat sheet ---------------------------- */

  _renderSheet() {
    const w = this.cells[this.cursor];
    if (!w) return;
    const def = weaponRow(w.id)?.def ?? null;
    const unlocked = this.model.isUnlocked(w.id);
    const rank = rankOf(w);

    this.sheetHead.innerHTML = `
      <div class="lo-sheet-id">
        <div class="lo-sheet-class">${esc(w.className ?? '')}</div>
        <div class="lo-sheet-name">${esc(w.name)}</div>
      </div>
      ${rank ? `<div class="lo-sheet-rank"><i>RANK</i><b>${esc(rank)}</b></div>` : ''}
      <div class="lo-sheet-state ${unlocked ? 'is-ok' : 'is-locked'}">${
        unlocked
          ? w.level === LEVEL_INITIAL
            ? 'STANDARD ISSUE'
            : `UNLOCKED · LEVEL ${w.level}`
          : `LOCKED · LEVEL ${w.level}`
      }</div>
      <button class="lo-sheet-x" type="button" data-act="sheet-close">${closeLabel()}</button>`;

    this.sheetBody.innerHTML = def ? sheetBody(def, w) : sheetEmpty(w);
    this._renderSheetFoot();
  }

  _renderSheetFoot() {
    const w = this.cells[this.cursor];
    const pad = this.ctx.input?.pad?.connected;
    const slot = this.slot.toUpperCase();
    this.sheetFoot.innerHTML = pad
      ? `<b>↕</b> SCROLL <s>·</s> <b>LB RB</b> WEAPON <s>·</s> <b>A</b> EQUIP ${slot} <s>·</s>
         <b>SELECT</b> SWITCH SLOT <s>·</s> <b>Y / B</b> BACK
         <s>·</s> <em>${esc(w?.name ?? '')}</em>`
      : `<b>↑↓</b> SCROLL <s>·</s> <b>←→</b> WEAPON <s>·</s> <b>ENTER</b> EQUIP ${slot} <s>·</s>
         <b>TAB</b> SWITCH SLOT <s>·</s> <b>ESC</b> BACK
         <s>·</s> <em>${esc(w?.name ?? '')}</em>`;
  }

  dispose() {
    this._unregister?.();
    this._observer?.disconnect();
    window.removeEventListener('keydown', this._onKey, true);
    this.root.remove();
  }
}

/* ============================================================================ *
 * The 性能詳細 sheet.
 *
 * Everything below is pure: a weapon definition in, a string of markup out. It runs
 * once when the sheet opens and never in a frame.
 *
 * ## Provenance
 *
 * A stats screen that silently mixes numbers taken from a published source with
 * numbers invented for this game is misleading, so every printed value carries one of
 * three marks:
 *
 *   ◆ SOURCE  — published by the reference, verbatim
 *   ∑ DERIVED — arithmetic on top of the published numbers
 *   ◇ GAME    — authored for this game
 *
 * These are not guesses. `weapons.stats.js` assembles every weapon out of three
 * labelled blocks — SCRAPED, DERIVED, AUTHORED — and `fieldOrigin` reads which block
 * a field actually came from. The category table below is only reached when that
 * module cannot be read at all, and a mark that came from it is drawn hollow, so
 * "we were told" and "we assumed" never look the same.
 * ============================================================================ */

const SRC = 'src';
const GAME = 'game';
const CALC = 'calc';

/**
 * Fallback provenance by field, used only when the weapon stats module cannot be
 * read. The split is the obvious one: a shooter's published stat card carries the
 * damage bands, the magazine and the TTK; cone angles, bloom, recoil and penetration
 * are tuning.
 */
const PROV_DEFAULT = {
  damage: SRC,
  magSize: SRC,
  ttk: SRC,
  rank: SRC,
  rpm: CALC,
  reserve: CALC,
  headMult: CALC,
  fireMode: GAME,
  reload: GAME,
  ads: GAME,
  sprintOut: GAME,
  muzzleVelocity: GAME,
  spread: GAME,
  bloom: GAME,
  recoil: GAME,
  penetration: GAME,
  switch: GAME,
  pellets: GAME,
  burst: GAME,
};

const PROV_GLYPH = { src: '◆', game: '◇', calc: '∑' };
const PROV_TITLE = {
  src: 'PUBLISHED BY THE SOURCE, VERBATIM',
  game: 'AUTHORED FOR THIS GAME',
  calc: 'DERIVED FROM THE PUBLISHED NUMBERS',
};

/**
 * A row builder bound to one weapon's provenance facts.
 *
 * Bound rather than passed per call because every row in the sheet asks the same
 * question of the same three blocks, and threading `facts` through forty call sites
 * is how one of them ends up asking about a different weapon.
 */
function rowsFor(facts) {
  const mark = (key, override) => {
    const known = override ?? fieldOrigin(facts, key);
    const p = known ?? PROV_DEFAULT[key] ?? GAME;
    const cls = `lo-p is-${p}${known ? '' : ' is-assumed'}`;
    const title = PROV_TITLE[p] + (known ? '' : ' — ASSUMED; THE DATASET DID NOT SAY');
    return `<em class="${cls}" title="${title}">${PROV_GLYPH[p]}</em>`;
  };
  const row = (label, value, key, override) =>
    `<div class="lo-srow"><span>${esc(label)}</span><b>${
      value == null || value === '' ? '—' : value
    }</b>${mark(key, override)}</div>`;
  return { row, mark };
}

function sheetEmpty(w) {
  return `<div class="lo-sheet-none">
      NO DEFINITION FOR <b>${esc(w?.id ?? '?')}</b>.<br>
      This weapon is on the ladder but has not been built yet.
    </div>`;
}

function sheetBody(def, w) {
  const facts = weaponFacts(w.id);
  const R = rowsFor(facts);
  const page = sourcePage(w.id);
  return `
    <div class="lo-legend">
      <span><em class="lo-p is-src">◆</em>PUBLISHED — VERBATIM</span>
      <span><em class="lo-p is-calc">∑</em>DERIVED FROM PUBLISHED</span>
      <span><em class="lo-p is-game">◇</em>AUTHORED FOR THIS GAME</span>
      ${
        facts.known
          ? page
            ? `<span class="lo-legend-src">SOURCE · ${esc(hostOf(page))}</span>`
            : ''
          : '<span><em class="lo-p is-src is-assumed">◆</em>HOLLOW = ASSUMED</span>'
      }
    </div>
    <div class="lo-sheet-grid">
      ${lethality(def, w, facts, R)}
      ${handling(def, facts, R)}
      ${accuracy(def, facts, R)}
      ${ballistics(def, facts, R)}
    </div>`;
}

/* --------------------------------- lethality -------------------------------- */

function lethality(def, w, facts, R) {
  const pts = normDamage(def);
  const pellets = num(def.pellets, 1) || 1;
  const hp = COMBAT.health;
  // The reconciled head multiplier, not the raw hitbox: the dataset scales each
  // weapon's `hitboxScale.head` so the engine's ×3.0 head lands on the multiplier the
  // published headshot TTK implies. Printing the ×3.0 would be telling the player
  // something no weapon in the game actually does.
  const headMult = facts.headMult ?? COMBAT.headHitbox * num(def.hitboxScale?.head, 1);

  // Prefer the source's own published step bands over anything reconstructed from the
  // engine curve: the curve carries 2 m interpolation seams the source does not, and
  // this table is meant to be the source's table.
  const published = Array.isArray(facts.scraped?.bands) ? facts.scraped.bands : null;
  const bandRows = (published ? publishedBands(published) : damageBands(pts)).map((b) => {
    const hi = stk(b.from * pellets, hp);
    const lo = stk(b.to * pellets, hp);
    const t = ttkFor(hi, def);
    return `<div class="lo-band">
        <span class="lo-band-r">${b.label}</span>
        <b class="lo-band-d">${b.value}</b>
        <span class="lo-band-s">${hi === lo ? `${hi}` : `${hi}–${lo}`} STK</span>
        <span class="lo-band-t">${t == null ? '—' : `${Math.round(t * 1000)} ms`}</span>
      </div>`;
  });

  const near = pts.length ? pts[0][1] * pellets : 0;
  const far = pts.length ? pts[pts.length - 1][1] * pellets : 0;
  const bodySTK = stk(near, hp);
  const headSTK = stk(near * headMult, hp);
  const pubBody = seconds(def.ttk?.body);
  const pubHead = seconds(def.ttk?.head);
  const calcBody = ttkFor(bodySTK, def);
  const calcHead = ttkFor(headSTK, def);
  // Two ways the published pair can disagree with what this weapon actually does, and
  // both are worth naming rather than papering over — that is the entire point of
  // marking provenance.
  //
  //   1. a handful of headshot TTKs are longer than the same weapon's bodyshot TTK,
  //      which cannot happen at one fire rate;
  //   2. a published TTK of 0 means "one shot" on the reference, and a few weapons
  //      carry it while doing less than a full health bar of damage here.
  const ttkInverted = pubBody != null && pubHead != null && pubHead > pubBody;
  const ttkOneShot = pubBody === 0 && bodySTK > 1;
  const ttkConflict = ttkInverted || ttkOneShot;

  return section(
    'LETHALITY',
    `${
      bandRows.length
        ? `<div class="lo-bands">
             <div class="lo-band is-head">
               <span class="lo-band-r">RANGE BAND</span><b class="lo-band-d">DMG</b>
               <span class="lo-band-s">SHOTS</span><span class="lo-band-t">TTK</span>
             </div>
             ${bandRows.join('')}
           </div>`
        : '<div class="lo-sheet-none">NO DAMAGE PROFILE</div>'
    }
     ${damageChart(def, pts, pellets, published, hp)}
     ${R.row('BODY TTK · PUBLISHED', pubTtk(pubBody), 'ttk')}
     ${R.row('HEAD TTK · PUBLISHED', pubTtk(pubHead), 'ttk')}
     ${R.row('BODY TTK · IN GAME', ms(calcBody), 'ttk', CALC)}
     ${R.row('HEAD TTK · IN GAME', ms(calcHead), 'ttk', CALC)}
     ${R.row('SHOTS TO KILL · BODY', bodySTK ? `${bodySTK} @ ${Math.round(near)} DMG` : null, 'damage', CALC)}
     ${R.row('SHOTS TO KILL · HEAD', headSTK ? `${headSTK} @ ${Math.round(near * headMult)} DMG` : null, 'damage', CALC)}
     ${R.row('HEAD MULTIPLIER', headMult ? `× ${round(headMult, 3)}` : null, 'headMult')}
     ${R.row('DAMAGE AT MAX RANGE', far ? `${Math.round(far)} · ${stk(far, hp)} STK` : null, 'damage', CALC)}
     ${pellets > 1 ? R.row('PELLETS PER SHELL', `${pellets} × ${pts[0]?.[1] ?? '—'}`, 'pellets') : ''}
     ${R.row('PUBLISHED RANK', rankOf(w), 'rank')}
     ${
       ttkConflict
         ? `<div class="lo-note is-warn">${
             ttkInverted
               ? `The source publishes a headshot TTK longer than this weapon's bodyshot TTK,
                  which is impossible at one fire rate.`
               : `The source publishes this weapon as a one-shot kill, which its own published
                  damage does not support against ${hp} health.`
           } The published pair is shown as printed; the in-game rows are what this weapon
              actually does here.</div>`
         : ''
     }
     <div class="lo-note">Shots-to-kill assume ${hp} health${
       pellets > 1 ? ' and every pellet on target' : ''
     }, and the head rows the × ${round(headMult, 2)} multiplier this weapon was reconciled
       onto. TTK is measured from the first round landing.</div>`
  );
}

/* --------------------------------- handling --------------------------------- */

function handling(def, facts, R) {
  const rl = def.reload ?? {};
  const ads = def.ads ?? {};
  const erg = def.ergonomics ?? {};
  const mag = num(def.magSize);
  const rpm = num(def.rpm);
  return section(
    'HANDLING',
    `${R.row('RATE OF FIRE', rpm ? `${Math.round(rpm)} RPM` : null, 'rpm')}
     ${R.row('FIRE MODE', String(def.fireMode ?? '').toUpperCase() || null, 'fireMode')}
     ${def.burstCount ? R.row('BURST', `${def.burstCount} RND · ${Math.round(num(def.burstDelay) * 1000)} ms GAP`, 'burst') : ''}
     ${R.row('MAGAZINE', mag ? `${mag} RND` : null, 'magSize')}
     ${R.row('RESERVE', def.startReserve ? `${def.startReserve} · MAX ${def.maxReserve ?? '—'}` : null, 'reserve')}
     ${R.row('MAG DURATION', mag && rpm ? `${round((mag / rpm) * 60, 2)} s` : null, 'rpm', CALC)}
     ${R.row('RELOAD · TACTICAL', s(rl.tactical), 'reload')}
     ${R.row('RELOAD · EMPTY', s(rl.empty), 'reload')}
     ${def.shellByShell ? R.row('RELOAD STYLE', 'SHELL BY SHELL', 'reload') : ''}
     ${def.boltCycle ? R.row('BOLT CYCLE', s(def.boltCycle), 'fireMode') : ''}
     ${def.pumpTime ? R.row('PUMP CYCLE', s(def.pumpTime), 'fireMode') : ''}
     ${R.row('ADS IN', s(ads.in), 'ads')}
     ${R.row('ADS OUT', s(ads.out), 'ads')}
     ${R.row('MAGNIFICATION', ads.fovScale ? `${round(1 / ads.fovScale, 2)}×` : null, 'ads', CALC)}
     ${R.row('SPRINT-OUT', s(erg.sprintOut), 'sprintOut')}
     ${R.row('SWITCH IN', s(erg.switchIn), 'switch')}
     ${R.row('SWITCH OUT', s(erg.switchOut), 'switch')}
     ${R.row('ADS MOVE SPEED', erg.adsMoveScale ? `${Math.round(erg.adsMoveScale * 100)} %` : null, 'switch')}`
  );
}

/* --------------------------------- accuracy --------------------------------- */

function accuracy(def, facts, R) {
  const sp = def.spread ?? {};
  const rc = def.recoil ?? {};
  const rpm = num(def.rpm);
  // Bloom has to out-run its own decay at the weapon's own fire rate or the cone can
  // never leave zero, so the net figure is the one worth printing.
  const net = sp.perShot != null && rpm ? sp.perShot - (sp.bloomDecay ?? 0) * (60 / rpm) : null;
  const climb = climbOverMag(def);
  const drift = driftOverMag(def);
  return section(
    'ACCURACY',
    `${R.row('HIP CONE', mrad(sp.hip), 'spread')}
     ${R.row('HIP · MOVING', mrad(sp.hipMoving), 'spread')}
     ${R.row('HIP · SPRINTING', mrad(sp.hipSprint), 'spread')}
     ${R.row('ADS CONE', sp.ads === 0 ? '0.00 mrad · EXACT' : mrad(sp.ads), 'spread')}
     ${R.row('ADS · MOVING', mrad(sp.adsMoving), 'spread')}
     ${def.pelletSpread != null ? R.row('PELLET SPREAD', mrad(def.pelletSpread), 'pellets') : ''}
     ${R.row('CROUCH', sp.crouchScale ? `× ${round(sp.crouchScale, 2)}` : null, 'spread')}
     ${R.row('AIRBORNE', sp.airScale ? `× ${round(sp.airScale, 2)}` : null, 'spread')}
     ${
       // A weapon with no bloom ceiling is deliberately bloom-free — the pump and the
       // bolt gate it to about a round a second, so a per-shot cone penalty would be a
       // tax with nothing to tax. Printing its decay rate, and a net of minus a quarter
       // of a radian per shot, is three rows of noise about a mechanic it does not have.
       sp.maxBloom > 0
         ? `${R.row('BLOOM PER SHOT', mrad(sp.perShot), 'bloom')}
            ${R.row('BLOOM CEILING', mrad(sp.maxBloom), 'bloom')}
            ${R.row('BLOOM DECAY', sp.bloomDecay ? `${round(sp.bloomDecay * 1000, 1)} mrad/s` : null, 'bloom')}
            ${R.row('BLOOM · NET AT ROF', net == null ? null : `${net > 0 ? '+' : ''}${round(net * 1000, 2)} mrad/shot`, 'bloom', CALC)}
            ${R.row('BLOOM RECOVERY', sp.bloomDecay ? `${round(sp.maxBloom / sp.bloomDecay, 2)} s` : null, 'bloom', CALC)}`
         : R.row('BLOOM', 'NONE — GATED BY THE ACTION', 'bloom')
     }
     ${R.row('RECOIL PITCH · PER SHOT', deg(rc.pitch), 'recoil')}
     ${R.row('RECOIL YAW · PER SHOT', deg(rc.yaw), 'recoil')}
     ${R.row('RECOIL RANDOM', rc.random != null ? `${Math.round(rc.random * 100)} %` : null, 'recoil')}
     ${R.row('RECOVERY', rc.recovery ? `${round(rc.recovery, 1)} /s` : null, 'recoil')}
     ${R.row('CLIMB OVER A MAGAZINE', climb == null ? null : `${round(climb, 1)}°`, 'recoil', CALC)}
     ${R.row('LATERAL DRIFT · MAGAZINE', drift == null ? null : `${round(drift, 1)}°`, 'recoil', CALC)}
     ${R.row('ADS RECOIL', rc.adsScale ? `× ${round(rc.adsScale, 2)}` : null, 'recoil')}
     <div class="lo-note">Cones are half-angles: 1 mrad is 1 cm of spread at 10 m. Climb is the
       running sum of this weapon's recoil pattern over ${num(def.magSize) || '—'} rounds hip-fired,
       drift the net sideways travel across the same magazine.</div>`
  );
}

/* -------------------------------- ballistics -------------------------------- */

function ballistics(def, facts, R) {
  const pen = num(def.penetration);
  return section(
    'BALLISTICS',
    `${R.row('MUZZLE VELOCITY', def.muzzleVelocity ? `${Math.round(def.muzzleVelocity)} m/s` : null, 'muzzleVelocity')}
     ${R.row('TRAVEL', def.projectile ? 'SIMULATED — LEAD REQUIRED' : 'HITSCAN', 'muzzleVelocity')}
     ${R.row('TIME OF FLIGHT · 50 m', def.muzzleVelocity ? `${Math.round((50 / def.muzzleVelocity) * 1000)} ms` : null, 'muzzleVelocity', CALC)}
     ${R.row('PENETRATION', pen ? `${round(pen, 2)} units` : null, 'penetration')}
     ${R.row('THROUGH GLASS', pen ? `${Math.round((pen / 1.2) * 100)} cm` : null, 'penetration', CALC)}
     ${R.row('THROUGH WOOD', pen ? `${Math.round((pen / 3.6) * 100)} cm` : null, 'penetration', CALC)}
     ${R.row('THROUGH METAL', pen ? `${Math.round((pen / 26) * 100)} cm` : null, 'penetration', CALC)}
     ${R.row('THROUGH CONCRETE', pen ? 'NONE' : null, 'penetration', CALC)}
     ${
       def.hitboxScale
         ? R.row(
             'HITBOX MULTIPLIERS',
             Object.entries(def.hitboxScale)
               .map(([k, v]) => `${k.toUpperCase()} ×${round(v, 2)}`)
               .join(' · '),
             'headMult'
           )
         : ''
     }
     <div class="lo-note">Penetration depths apply this game's material costs — glass 1.2, wood 3.6,
       metal 26 units per metre, concrete impassable — to the weapon's own figure.</div>`
  );
}


function section(title, inner) {
  return `<section class="lo-sec"><h3>${title}</h3>${inner}</section>`;
}

/* ----------------------------- the damage chart ----------------------------- */

/**
 * Damage over distance, with the shots-to-kill steps behind it.
 *
 * The curve alone tells a player that damage falls off. The STK bands tell them the
 * only thing they actually feel: the range at which the same burst stops killing.
 * The two are drawn together because the interesting moments are where the curve
 * crosses a step boundary, and neither picture shows that on its own.
 *
 * Pure SVG built as a string, once, on open. No layout is read.
 */
function damageChart(def, pts, pellets, published, hp) {
  if (!pts || pts.length < 2) return '';
  const W = 340;
  const H = 158;
  // Left gutter wide enough for a three-digit damage label: a sniper peaks at 118 and
  // a shotgun's all-pellet total is higher still.
  const L = 32;
  const R = 10;
  const T = 16;
  const B = 26;

  // The engine curve is held flat out to 200 m so `damageAtRange` never runs off the
  // end. Plotting all 200 would spend three quarters of the chart on a straight line
  // no player will ever be at, so the axis stops a little past the last published
  // band — where the weapon has finished doing anything interesting.
  const curveEnd = pts[pts.length - 1][0];
  const lastBand = published?.length ? Number(published[published.length - 1][0]) || 0 : 0;
  const maxD = Math.max(1, lastBand > 0 ? Math.min(curveEnd, Math.max(30, lastBand * 1.7)) : curveEnd);
  const peak = Math.max(...pts.map((q) => q[1])) * pellets;
  const maxV = Math.max(1, peak * 1.18);
  const px = (d) => n1(L + (Math.min(d, maxD) / maxD) * (W - L - R));
  const py = (v) => n1(T + (1 - Math.min(v, maxV) / maxV) * (H - T - B));

  // Sample the curve to find where the shots-to-kill number changes. A metre of
  // resolution over any sane map is well below the eye's ability to care.
  const steps = [];
  const stepCount = Math.max(24, Math.min(400, Math.ceil(maxD)));
  let run = null;
  for (let i = 0; i <= stepCount; i++) {
    const d = (i / stepCount) * maxD;
    const n = stk(interp(pts, d) * pellets, hp);
    if (!run || run.n !== n) {
      if (run) run.to = d;
      run = { n, from: d, to: maxD };
      steps.push(run);
    }
  }
  if (run) run.to = maxD;

  const bands = steps
    .map((sRun, i) => {
      const x0 = px(sRun.from);
      const x1 = px(sRun.to);
      const wBand = Math.max(0, x1 - x0);
      const label =
        wBand > 26
          ? `<text class="lo-ch-stk" x="${n1((x0 + x1) / 2)}" y="${T - 5}" text-anchor="middle">${sRun.n} STK</text>`
          : '';
      return `<rect class="lo-ch-band${i % 2 ? ' is-alt' : ''}" x="${x0}" y="${T}" width="${n1(wBand)}" height="${H - T - B}"></rect>${
        i > 0 ? `<line class="lo-ch-step" x1="${x0}" y1="${T}" x2="${x0}" y2="${H - B}"></line>` : ''
      }${label}`;
    })
    .join('');

  // Axis ticks are the *published* band boundaries where we have them — those are the
  // distances the source names and the player will hear quoted — not the engine
  // curve's control points, which include 2 m interpolation seams that mean nothing.
  const marks = published?.length
    ? published.map(([d]) => Number(d) || 0).concat(maxD)
    : pts.map(([d]) => d);
  const seen = new Set();
  const ticks = marks
    .map((d) => {
      if (d > maxD) return '';
      const key = Math.round(d);
      if (seen.has(key)) return '';
      seen.add(key);
      const anchor = key === 0 ? 'start' : key >= Math.round(maxD) ? 'end' : 'middle';
      return `${
        key === 0 ? '' : `<line class="lo-ch-tick" x1="${px(d)}" y1="${H - B}" x2="${px(d)}" y2="${H - B + 4}"></line>`
      }<text class="lo-ch-x" x="${px(d)}" y="${H - 8}" text-anchor="${anchor}">${key}</text>`;
    })
    .join('');

  // Clip the drawn curve to the axis so a weapon whose curve runs to 200 m does not
  // paint a tail across the right-hand margin.
  const drawn = pts.filter(([d]) => d <= maxD);
  if (!drawn.length || drawn[drawn.length - 1][0] < maxD) {
    drawn.push([maxD, interp(pts, maxD)]);
  }
  const line = drawn
    .map(([d, v], i) => `${i ? 'L' : 'M'}${px(d)},${py(v * pellets)}`)
    .join(' ');
  const area = `${line} L${px(maxD)},${H - B} L${L},${H - B} Z`;
  const dots = drawn
    .map(([d, v]) => `<circle class="lo-ch-dot" cx="${px(d)}" cy="${py(v * pellets)}" r="2.1"></circle>`)
    .join('');

  const gy = [0, 0.5, 1]
    .map((f) => {
      const v = maxV * f;
      return `<line class="lo-ch-grid" x1="${L}" y1="${py(v)}" x2="${W - R}" y2="${py(v)}"></line>
              <text class="lo-ch-y" x="${L - 5}" y="${py(v) + 3}" text-anchor="end">${Math.round(v)}</text>`;
    })
    .join('');

  return `<figure class="lo-chart">
      <figcaption>DAMAGE OVER DISTANCE${pellets > 1 ? ' · ALL PELLETS' : ''}<i>◆ CURVE · ∑ STK STEPS</i></figcaption>
      <svg viewBox="0 0 ${W} ${H}" role="img" preserveAspectRatio="xMidYMid meet"
           aria-label="Damage over distance with shots-to-kill steps">
        ${bands}${gy}
        <path class="lo-ch-area" d="${area}"></path>
        <path class="lo-ch-line" d="${line}"></path>
        ${dots}${ticks}
      </svg>
      <div class="lo-chart-x">METRES</div>
    </figure>`;
}

/* ------------------------------- number helpers ----------------------------- */

/**
 * Round to one decimal and stay a *number*.
 *
 * `round()` below returns a string because it feeds text; using it for SVG geometry
 * makes `round(y) + 3` produce "1323" from 132, which places the label a thousand
 * units off-canvas and is invisible rather than obviously wrong. Coordinates get this.
 */
const n1 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 10) / 10 : 0;
};

/** `[[distance, damage], ...]`, sorted and sanitised. Empty for unusable data. */
function normDamage(def) {
  const raw = def?.damage;
  if (!Array.isArray(raw)) return [];
  const pts = raw
    .map((p) => (Array.isArray(p) ? [Number(p[0]), Number(p[1])] : null))
    .filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  pts.sort((a, b) => a[0] - b[0]);
  return pts;
}

/** Damage at `d`, linear between control points, flat outside them. */
function interp(pts, d) {
  if (!pts.length) return 0;
  if (d <= pts[0][0]) return pts[0][1];
  for (let i = 1; i < pts.length; i++) {
    if (d <= pts[i][0]) {
      const [d0, v0] = pts[i - 1];
      const [d1, v1] = pts[i];
      const t = (d - d0) / Math.max(1e-6, d1 - d0);
      return v0 + (v1 - v0) * t;
    }
  }
  return pts[pts.length - 1][1];
}

/**
 * The published range bands, read off the control points.
 *
 * A pair with the same damage at both ends is a flat band and prints one number; a
 * pair that falls prints "34 → 26", because pretending a slope is a step would be a
 * different weapon.
 */
function damageBands(pts) {
  const out = [];
  for (let i = 1; i < pts.length; i++) {
    const [d0, v0] = pts[i - 1];
    const [d1, v1] = pts[i];
    if (d1 === d0) continue;
    out.push({
      label: `${Math.round(d0)}–${Math.round(d1)} m`,
      value: v0 === v1 ? String(round(v0, 1)) : `${round(v0, 1)} → ${round(v1, 1)}`,
      from: v0,
      to: v1,
    });
  }
  const last = pts[pts.length - 1];
  if (last) {
    out.push({
      label: `${Math.round(last[0])} m +`,
      value: String(round(last[1], 1)),
      from: last[1],
      to: last[1],
    });
  }
  return out;
}

const stk = (dmg, hp = COMBAT.health) => (dmg > 0 ? Math.max(1, Math.ceil(hp / dmg)) : 0);

/**
 * Time to land `shots`, counting from the first round hitting.
 *
 * The gap between rounds is not always `60 / rpm`: a bolt gun is gated by its bolt
 * cycle and a pump by its pump, and a burst weapon pays a dead delay after each
 * completed burst. Using the fire rate alone would quote a bolt-action sniper a
 * double-tap 200 ms faster than it can physically manage.
 */
function ttkFor(shots, def) {
  const rpm = num(def?.rpm);
  if (!shots || shots < 1 || !rpm) return null;
  const n = shots - 1;
  if (n === 0) return 0;
  if (def.fireMode === 'bolt') return n * (num(def.boltCycle) || 60 / rpm);
  if (def.fireMode === 'pump') return n * (num(def.pumpTime) || 60 / rpm);
  const iv = 60 / rpm;
  if (def.fireMode === 'burst' && num(def.burstCount) > 0) {
    // One inter-burst delay for every burst that completes before the killing round.
    return n * iv + Math.floor(n / num(def.burstCount)) * num(def.burstDelay);
  }
  return n * iv;
}

/**
 * The source's published range bands, verbatim.
 *
 * `[[0, 26], [21, 24], [41, 22]]` reads as "26 up to 21 m, 24 up to 41 m, 22 beyond",
 * so each row's span runs from its own distance to the *next* one. These are steps,
 * not a curve; the engine's interpolated version is what the chart draws.
 */
function publishedBands(bands) {
  const out = [];
  for (let i = 0; i < bands.length; i++) {
    const [d0, v] = bands[i];
    const next = bands[i + 1];
    // A published table sometimes repeats its opening distance (0 and 3 m on the
    // shotguns); a zero-width band is noise, not information.
    if (next && Number(next[0]) === Number(d0)) continue;
    out.push({
      label: next ? `${Math.round(d0)}–${Math.round(next[0])} m` : `${Math.round(d0)} m +`,
      value: String(round(v, 1)),
      from: v,
      to: v,
    });
  }
  return out;
}

/** The host of a source URL, for crediting it without printing a whole link. */
function hostOf(url) {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return String(url ?? '').slice(0, 40);
  }
}

/** Total muzzle climb, in degrees, over one magazine of hip fire. */
function climbOverMag(def) {
  const pat = def?.pattern;
  const pitch = num(def?.recoil?.pitch);
  if (!Array.isArray(pat) || !pat.length || !pitch) return null;
  const n = Math.max(1, Math.min(200, num(def.magSize, pat.length)));
  let sum = 0;
  for (let i = 0; i < n; i++) sum += Math.abs(num(pat[Math.min(i, pat.length - 1)]?.[0]));
  return (sum * pitch * 180) / Math.PI;
}

/** Net horizontal displacement, in degrees, over one magazine. Signed sum, absolute. */
function driftOverMag(def) {
  const pat = def?.pattern;
  const yaw = num(def?.recoil?.yaw);
  if (!Array.isArray(pat) || !pat.length || !yaw) return null;
  const n = Math.max(1, Math.min(200, num(def.magSize, pat.length)));
  let sum = 0;
  for (let i = 0; i < n; i++) sum += num(pat[Math.min(i, pat.length - 1)]?.[1]);
  return (Math.abs(sum) * yaw * 180) / Math.PI;
}

/**
 * The weapon-level line on the detail panel.
 *
 * Two levels exist and they are not the same number: the rank at the top of this
 * screen is the *career* level that decides whether this gun is yours at all, and
 * this one is the *weapon* level that decides which parts you may bolt to it. They
 * are drawn in different places and labelled differently for exactly that reason.
 * Absent Gunsmith, absent line — never a placeholder implying a feature that is not
 * wired up.
 */
function gunsmithLine(gunsmith, w, unlocked) {
  if (!gunsmith || !unlocked || w.pending) return '';
  const s = gunsmith.summaryFor(w.id);
  if (!s) return '';
  return `<div class="lo-gs-line${s.maxed ? ' is-max' : ''}">
      <span class="lo-gs-lv"><i>WPN LV</i><b>${s.maxed ? WEAPON_MAX_LEVEL : s.level}</b></span>
      <span class="lo-gs-track"><i style="transform:scaleX(${s.fraction.toFixed(3)})"></i></span>
      <span class="lo-gs-fit">${s.fitted} / ${s.limit}</span>
    </div>`;
}

function metaLine(w, unlocked) {
  if (w.pending) return 'COMING SOON';
  if (!unlocked) return w.level === LEVEL_INITIAL ? 'STANDARD ISSUE' : `LOCKED · LEVEL ${w.level}`;
  const mag = w.stats?.magSize;
  const mode = String(w.stats?.fireMode ?? '').toUpperCase();
  const bits = [mag ? `${mag} RND` : null, mode || null].filter(Boolean);
  return bits.length ? bits.join(' · ') : 'AVAILABLE';
}

function rankOf(w) {
  return w?.rank ?? weaponRow(w?.id)?.rank ?? null;
}

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const round = (v, p = 1) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const k = 10 ** p;
  return String(Math.round(n * k) / k);
};
const s = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? `${round(v, 2)} s` : null);
const ms = (v) => (v == null || !Number.isFinite(Number(v)) ? null : `${Math.round(Number(v) * 1000)} ms`);
/** A published TTK. Zero is the reference's way of writing "one shot", not a blank. */
const pubTtk = (v) => (v == null ? null : v === 0 ? '0 ms · ONE SHOT' : ms(v));
const mrad = (v) => (Number.isFinite(Number(v)) ? `${round(Number(v) * 1000, 2)} mrad` : null);
const deg = (v) => (Number.isFinite(Number(v)) && Number(v) !== 0 ? `${round((Number(v) * 180) / Math.PI, 2)}°` : null);

const other = (slot) => (slot === 'primary' ? 'secondary' : 'primary');
const fmt = (n) => Number(n || 0).toLocaleString('en-US');

function esc(s2) {
  return String(s2 ?? '').replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]
  );
}
